// MediCarePharmacyLayout.js - Beautiful Medical Store Theme with Full Image Editing Support
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component - FIXED with p.themeText support
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    // Check multiple sources for the value
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-blue-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-blue-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content") || field.toLowerCase().includes("fullcontent");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function MediCarePharmacyLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#2563EB"; // Medical Blue
  const secondary = T.secondaryColor || "#3B82F6"; // Light Blue
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedHealthTip, setSelectedHealthTip] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [isPulseAnimating, setIsPulseAnimating] = useState(true);
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Hero image fields that should be protected from editing
  const HERO_IMAGE_FIELDS = [
    'themePreviewImage',
    'medicareHeroImage1',
    'medicareHeroImage2',
    'medicareHeroImage3',
    'medicareHeroImage4'
  ];

  // Medical hero images - DIRECTLY FROM CONFIG
  const medicalHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ee9cd1cb-f62c-4b0d-9b9d-89df1d1142b3_medicare1.jpg",
    },
    {
      field: "medicareHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/8ca01058-5510-4b22-964d-d368cd1a809b_med2.jpg",
    },
    {
      field: "medicareHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1ecf71dc-cc2d-48e4-82c6-38f647e4d845_med3.jpg",
    },
    {
      field: "medicareHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5e971433-f614-4bfe-9da4-08f9957bc803_med4.jpg",
    },
  ];

  // Medical Products - Added imageField support
  const medicalProducts = [
    // Medicines
    {
      id: 1,
      nameKey: "product1_name",
      name: "Paracetamol 500mg (10 tablets)",
      priceKey: "product1_price",
      price: 45,
      originalPriceKey: "product1_original_price",
      originalPrice: 65,
      imageField: "product1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/68aa388a-4b25-4aa0-9d79-0cdc386534db_Paracetamol_500mg.webp",
      categoryKey: "product1_category",
      category: "Medicines",
      subCategory: "Pain Relief",
      descriptionKey: "product1_description",
      description: "Effective pain relief for headaches, fever, and body aches. Trusted by doctors worldwide.",
      badge: "Essential",
      badgeColor: "#2563EB",
      quantity: "10 tablets",
      brand: "MediTrust",
      type: "OTC",
      dosage: "500mg"
    },
    {
      id: 2,
      nameKey: "product2_name",
      name: "Vitamin C 1000mg (30 tablets)",
      priceKey: "product2_price",
      price: 299,
      originalPriceKey: "product2_original_price",
      originalPrice: 399,
      imageField: "product2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b69db706-083b-407b-a5a8-c5c9f5cc7720_Vitamin_C_1000mg.webp",
      categoryKey: "product2_category",
      category: "Vitamins",
      subCategory: "Immune Support",
      descriptionKey: "product2_description",
      description: "High-strength Vitamin C for immune system support and antioxidant protection.",
      badge: "Premium",
      badgeColor: "#10B981",
      quantity: "30 tablets",
      brand: "NutriHealth",
      type: "Supplement",
      dosage: "1000mg"
    },
    {
      id: 3,
      nameKey: "product3_name",
      name: "Multivitamin Complex (60 capsules)",
      priceKey: "product3_price",
      price: 499,
      originalPriceKey: "product3_original_price",
      originalPrice: 649,
      imageField: "product3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/48d52fa6-fcff-4a22-abe4-fd72978d18ff_Multivitamin_Complex.jpg",
      categoryKey: "product3_category",
      category: "Vitamins",
      subCategory: "Multivitamins",
      descriptionKey: "product3_description",
      description: "Complete multivitamin complex with essential minerals for daily wellness.",
      badge: "Best Seller",
      badgeColor: "#F59E0B",
      quantity: "60 capsules",
      brand: "WellnessPlus",
      type: "Supplement",
      dosage: "Daily"
    },
    
    // Medical Equipment
    {
      id: 4,
      nameKey: "product4_name",
      name: "Digital Blood Pressure Monitor",
      priceKey: "product4_price",
      price: 1999,
      originalPriceKey: "product4_original_price",
      originalPrice: 2999,
      imageField: "product4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/93cc364f-d3b5-40c0-ac49-a141f0caae2e_Digital_Blood_Pressure_Monitor.jpg",
      categoryKey: "product4_category",
      category: "Equipment",
      subCategory: "Monitoring",
      descriptionKey: "product4_description",
      description: "Accurate digital blood pressure monitor with large display and memory storage.",
      badge: "Medical Grade",
      badgeColor: "#2563EB",
      quantity: "1 unit",
      brand: "MediTech",
      type: "Digital",
      warranty: "2 Years"
    },
    {
      id: 5,
      nameKey: "product5_name",
      name: "Pulse Oximeter (Fingertip)",
      priceKey: "product5_price",
      price: 799,
      originalPriceKey: "product5_original_price",
      originalPrice: 1199,
      imageField: "product5_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/217f319c-a55f-41f1-bc43-b97240c3b2b6_Pulse_Oximeter_(Fingertip).jpg",
      categoryKey: "product5_category",
      category: "Equipment",
      subCategory: "Monitoring",
      descriptionKey: "product5_description",
      description: "Accurate pulse oximeter for oxygen saturation and heart rate monitoring.",
      badge: "Essential",
      badgeColor: "#10B981",
      quantity: "1 unit",
      brand: "HealthSense",
      type: "Digital",
      warranty: "1 Year"
    },
    {
      id: 6,
      nameKey: "product6_name",
      name: "Digital Thermometer (Infrared)",
      priceKey: "product6_price",
      price: 399,
      originalPriceKey: "product6_original_price",
      originalPrice: 599,
      imageField: "product6_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/8fa88137-ab84-4d4c-a85c-b746e7f77384_Digital_Thermometer_(Infrared).jpg",
      categoryKey: "product6_category",
      category: "Equipment",
      subCategory: "Thermometers",
      descriptionKey: "product6_description",
      description: "Non-contact infrared thermometer for quick and accurate temperature readings.",
      badge: "Fast",
      badgeColor: "#F59E0B",
      quantity: "1 unit",
      brand: "TempSafe",
      type: "Infrared",
      warranty: "6 Months"
    },
    
    // Personal Care
    {
      id: 7,
      nameKey: "product7_name",
      name: "Hand Sanitizer (500ml)",
      priceKey: "product7_price",
      price: 149,
      originalPriceKey: "product7_original_price",
      originalPrice: 199,
      imageField: "product7_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/f58d4aea-5e8e-4c76-be74-6da85cf79410_Hand_Sanitizer_(500ml).jpeg",
      categoryKey: "product7_category",
      category: "Personal Care",
      subCategory: "Hygiene",
      descriptionKey: "product7_description",
      description: "Alcohol-based hand sanitizer with moisturizing formula. Kills 99.9% germs.",
      badge: "Protection",
      badgeColor: "#2563EB",
      quantity: "500ml",
      brand: "SafeCare",
      type: "Sanitizer",
      alcohol: "70%"
    },
    {
      id: 8,
      nameKey: "product8_name",
      name: "Face Mask (50 Pack)",
      priceKey: "product8_price",
      price: 299,
      originalPriceKey: "product8_original_price",
      originalPrice: 449,
      imageField: "product8_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b816dd5c-137c-4aaa-b08b-eaa175b0bff9_Face_Mask_(50_Pack).jpg",
      categoryKey: "product8_category",
      category: "Personal Care",
      subCategory: "Protection",
      descriptionKey: "product8_description",
      description: "Premium 3-ply disposable face masks for maximum protection and comfort.",
      badge: "Premium",
      badgeColor: "#10B981",
      quantity: "50 pack",
      brand: "SafeShield",
      type: "Disposable",
      protection: "3-Ply"
    },
    {
      id: 9,
      nameKey: "product9_name",
      name: "First Aid Kit (Essential)",
      priceKey: "product9_price",
      price: 499,
      originalPriceKey: "product9_original_price",
      originalPrice: 699,
      imageField: "product9_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/79192176-70e1-48b1-bb7f-0cb2adc067a2_First_Aid_Kit_(Essential).jpg",
      categoryKey: "product9_category",
      category: "Personal Care",
      subCategory: "First Aid",
      descriptionKey: "product9_description",
      description: "Complete first aid kit with bandages, antiseptics, and emergency supplies.",
      badge: "Essential",
      badgeColor: "#F59E0B",
      quantity: "Kit",
      brand: "CareFirst",
      type: "Emergency",
      items: "25+ items"
    }
  ];

  // Medical Categories
  const medicalCategories = [
    { key: "all", icon: "💊", label: "All Products", color: "#2563EB" },
    { key: "medicines", icon: "💉", label: "Medicines", color: "#3B82F6" },
    { key: "vitamins", icon: "💪", label: "Vitamins", color: "#10B981" },
    { key: "equipment", icon: "🩺", label: "Equipment", color: "#8B5CF6" },
    { key: "personal_care", icon: "🧴", label: "Personal Care", color: "#F59E0B" },
  ];

  // Health Tips - Added editable fields
  const healthTips = [
    {
      id: 1,
      icon: "💧",
      titleKey: "health_tip_1_title",
      title: "Stay Hydrated",
      descKey: "health_tip_1_desc",
      desc: "Drink at least 8 glasses of water daily for optimal health.",
      color: "#2563EB"
    },
    {
      id: 2,
      icon: "🥗",
      titleKey: "health_tip_2_title",
      title: "Eat Healthy",
      descKey: "health_tip_2_desc",
      desc: "Include fruits, vegetables, and whole grains in your diet.",
      color: "#10B981"
    },
    {
      id: 3,
      icon: "🏃",
      titleKey: "health_tip_3_title",
      title: "Exercise Regular",
      descKey: "health_tip_3_desc",
      desc: "30 minutes of moderate exercise, 5 days a week.",
      color: "#F59E0B"
    },
    {
      id: 4,
      icon: "😴",
      titleKey: "health_tip_4_title",
      title: "Quality Sleep",
      descKey: "health_tip_4_desc",
      desc: "Aim for 7-8 hours of uninterrupted sleep each night.",
      color: "#8B5CF6"
    },
    {
      id: 5,
      icon: "🧘",
      titleKey: "health_tip_5_title",
      title: "Manage Stress",
      descKey: "health_tip_5_desc",
      desc: "Practice meditation or deep breathing for mental wellness.",
      color: "#EC4899"
    },
    {
      id: 6,
      icon: "🌿",
      titleKey: "health_tip_6_title",
      title: "Natural Supplements",
      descKey: "health_tip_6_desc",
      desc: "Consider natural supplements for immune support.",
      color: "#14B8A6"
    }
  ];

  // Health Journal Posts - Added imageField and all editable keys
  const healthPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "Understanding Your Immune System",
      descKey: "post1_desc",
      desc: "Boost your immunity naturally with these expert tips.",
      fullContentKey: "post1_content",
      fullContent: "Your immune system is your body's natural defense mechanism. Learn how to strengthen it through proper nutrition, regular exercise, adequate sleep, and stress management. Discover the key nutrients that support immune function and how to incorporate them into your daily routine.",
      imageField: "post1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/687f251f-cbf1-4f43-bc92-4ad24d0d3444_Understanding_Your_Immune_System.jpg",
      dateKey: "post1_date",
      date: "December 15, 2024",
      authorKey: "post1_author",
      author: "Dr. Sarah Johnson",
      category: "Immune Health"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "The Importance of Mental Health",
      descKey: "post2_desc",
      desc: "Taking care of your mind is as important as your body.",
      fullContentKey: "post2_content",
      fullContent: "Mental health is an essential component of overall wellness. Explore strategies for maintaining emotional balance, reducing anxiety, and building resilience. Learn about the mind-body connection and how mental wellness contributes to physical health.",
      imageField: "post2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/2e54097d-d01d-4a2d-af2c-e0160585e74d_The_Importance_of_Mental_Health.jpg",
      dateKey: "post2_date",
      date: "December 10, 2024",
      authorKey: "post2_author",
      author: "Dr. Michael Chen",
      category: "Mental Health"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Nutrition for Heart Health",
      descKey: "post3_desc",
      desc: "Eating for a healthy heart.",
      fullContentKey: "post3_content",
      fullContent: "Heart disease is preventable with the right lifestyle choices. Discover heart-healthy foods, beneficial supplements, and lifestyle changes that can significantly reduce your risk. Learn about the role of omega-3 fatty acids, antioxidants, and fiber in cardiovascular health.",
      imageField: "post3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d01a6eb2-0a10-498c-a0cb-32185a6eecf3_Nutrition_for_Heart_Health.jpg",
      dateKey: "post3_date",
      date: "December 5, 2024",
      authorKey: "post3_author",
      author: "Dr. Emily Rodriguez",
      category: "Heart Health"
    }
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_medicines", label: "MEDICINES", page: "medicines" },
    { key: "nav_vitamins", label: "VITAMINS", page: "vitamins" },
    { key: "nav_equipment", label: "EQUIPMENT", page: "equipment" },
    { key: "nav_health", label: "HEALTH TIPS", page: "health" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages with healthcare theme
  const topBarMessages = [
    "💊 YOUR HEALTH, OUR PRIORITY",
    "✦ FREE DELIVERY ON ORDERS OVER ₹500",
    "💪 100% AUTHENTIC PRODUCTS",
    "✦ EXPERT PHARMACY ADVICE AVAILABLE"
  ];

  // Floating particles - medical themed
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 2 + Math.random() * 4,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 5,
    opacity: 0.03 + Math.random() * 0.08,
    type: Math.random() > 0.5 ? 'plus' : 'circle'
  }));

  // Floating medical symbols
  const medicalSymbols = [
    { icon: "♥", delay: 0, duration: 8 },
    { icon: "⚕", delay: 2, duration: 10 },
    { icon: "♥", delay: 4, duration: 9 },
    { icon: "⚕", delay: 6, duration: 11 },
    { icon: "♥", delay: 1, duration: 7 },
    { icon: "⚕", delay: 3, duration: 12 },
  ];

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % medicalHeroImageConfigs.length);
    }, 5500);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 80 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openHealthTipModal = useCallback((tip, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedHealthTip(tip);
  }, [p.previewEditMode, p.activeEditField]);

  const closeHealthTipModal = useCallback(() => {
    setSelectedHealthTip(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedProduct(null);
      setSelectedPost(null);
      setSelectedHealthTip(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || medicalHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = medicalHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, medicalHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "MEDICARE PHARMACY",
    title: p.storeTheme?.heroTitle || "Your Health, Our Commitment",
    subtitle: p.storeTheme?.heroSubtitle || "Trusted healthcare products for your family",
    primaryCta: p.storeTheme?.primaryCtaText || "Shop Medicines",
    secondaryCta: p.storeTheme?.secondaryCtaText || "Health Tips",
  };

  // Get editable products with imageField
  const editableMedicalProducts = medicalProducts.map((product) => ({
    ...product,
    image: p.storeTheme?.[product.imageField] || product.image,
  }));

  // Get editable posts with imageField
  const editableHealthPosts = healthPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));

  // Get filtered items
  const getFilteredItems = (category) => {
    if (category === "all") return editableMedicalProducts;
    const map = {
      "medicines": "Medicines",
      "vitamins": "Vitamins",
      "equipment": "Equipment",
      "personal_care": "Personal Care",
    };
    return editableMedicalProducts.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -40, y: -15, rotate: -2 },
      { x: 40, y: -15, rotate: 2 },
      { x: -40, y: 15, rotate: -1 },
      { x: 40, y: 15, rotate: 1 },
    ];
    return directions[index % directions.length];
  };

  // Pulse animation for medical symbols
  useEffect(() => {
    const interval = setInterval(() => {
      setIsPulseAnimating(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-blue-50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Inter', sans-serif" }}>
      
      {/* Medical Floating Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={`absolute medical-particle ${particle.type === 'plus' ? 'plus-float' : 'circle-float'}`}
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size * 2}px`,
              height: `${particle.size * 2}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(15 + Math.random() * 25).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>

      {/* Floating Medical Symbols */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {medicalSymbols.map((symbol, idx) => (
          <div
            key={idx}
            className="absolute medical-symbol-float"
            style={{
              left: `${10 + idx * 18}%`,
              top: `${10 + Math.random() * 70}%`,
              fontSize: `${20 + Math.random() * 30}px`,
              color: `${primary}${Math.floor(10 + Math.random() * 15).toString(16)}`,
              animationDelay: `${symbol.delay}s`,
              animationDuration: `${symbol.duration}s`,
              opacity: 0.08,
            }}
          >
            {symbol.icon}
          </div>
        ))}
      </div>
      
      {/* Top Bar - Medical Style */}
      <div className="relative bg-blue-900 text-blue-200 py-2.5 overflow-hidden border-b-2 border-blue-700/30">
        <div className="flex items-center justify-center gap-6 whitespace-nowrap animate-marquee-slow">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-5 text-xs tracking-wider">
              <span className="mr-2">✦</span>
              <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - Medical Style */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-blue-900/95 backdrop-blur-xl shadow-lg py-2 border-b border-blue-700/20" : "bg-blue-900/90 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-xl border-2 border-blue-400/50 shadow-lg shadow-blue-700/20">
              <span className="text-white font-bold text-sm">+</span>
            </div>
            <span className="font-bold text-xl tracking-tight text-blue-100">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "MediCare"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-5">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  activePage === item.page 
                    ? "text-blue-300" 
                    : "text-blue-100/80 hover:text-blue-100"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-blue-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-blue-800/50">
                <FaHeart className="text-lg text-blue-100/80 hover:text-blue-100 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-blue-900 shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-blue-800/30">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => {
                  navigateTo(item.page, e);
                  setMobileMenuOpen(false);
                }}
                className={`text-sm py-2 px-3 rounded-lg text-left ${
                  activePage === item.page 
                    ? "bg-blue-800/30 text-blue-300" 
                    : "text-blue-100/80 hover:text-blue-100"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - Medical Professional - FIXED: DIRECTLY USING CONFIG */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full h-full">
              {medicalHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img 
                      src={p.storeTheme?.[img.field] || img.src} 
                      alt={`Medical ${idx + 1}`} 
                      className="w-full h-full object-cover object-center" 
                      style={{ 
                        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                        transition: "transform 0.3s ease-out" 
                      }} 
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-900/50 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-50 via-transparent to-transparent z-20 pointer-events-none" />
              
              {/* Hero edit buttons */}
              {p.previewEditMode && (
                <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-30 flex flex-wrap items-center gap-2 rounded-2xl border border-blue-300/40 bg-blue-900/35 px-3 py-2 backdrop-blur-sm">
                  {medicalHeroImageConfigs.map((image, index) => (
                    <button
                      key={image.field}
                      type="button"
                      onClick={(event) => handleEditHeroImage(event, image.field)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition ${index === activeSlide ? "bg-blue-500 text-white" : "bg-white/10 text-blue-100 hover:bg-white/20"}`}
                    >
                      Hero {index + 1}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Medical wave overlay */}
              <div className="absolute bottom-0 left-0 right-0 z-20">
                <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                  <path d="M0 60 C 360 0, 720 120, 1080 60 C 1260 30, 1380 90, 1440 60 L 1440 120 L 0 120 Z" fill="#EFF6FF" opacity="0.95" />
                </svg>
              </div>
            </div>
            
            <div className="relative z-30 h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-blue-800/40 backdrop-blur-md rounded-full border border-blue-400/30">
                    <span className="w-2 h-2 rounded-full bg-blue-400 animate-ping" />
                    <span className="text-blue-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.12}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-blue-100/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("medicines", e)} 
                    className="group px-8 py-4 rounded-full bg-blue-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="medicines"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("health", e)} 
                    className="px-8 py-4 rounded-full border-2 border-blue-400/50 text-blue-200 font-semibold tracking-wide transition-all duration-300 hover:bg-blue-600/30 hover:border-blue-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="health"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
              <div className="flex flex-col items-center gap-1">
                <span className="text-blue-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-blue-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-blue-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Medical Badge */}
          <div className="relative z-10 -mt-6 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-blue-200">
              <span className="text-blue-600 text-sm">💊</span>
              <span className="text-blue-900 text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="100% AUTHENTIC MEDICINES"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-blue-600 text-sm">💊</span>
            </div>
          </div>
          
          {/* Health Tips Quick Access */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="text-center mb-8">
              <span className="text-blue-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="health_tips_badge" 
                  value="Health Tips"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="health_tips_title" 
                  value="Daily Wellness Tips"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {healthTips.map((tip, idx) => (
                <div 
                  key={tip.id} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={() => openHealthTipModal(tip)}
                >
                  <div className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white border border-blue-200 hover:border-blue-400">
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      {tip.icon}
                    </div>
                    <h3 className="font-semibold text-blue-800 text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={tip.titleKey} 
                        value={tip.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: tip.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Category Grid - Medical Style */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {medicalCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("medicines", e);
                    } else {
                      setActiveCategory(cat.key);
                      const pageMap = {
                        "medicines": "medicines",
                        "vitamins": "vitamins",
                        "equipment": "equipment",
                        "personal_care": "personal_care",
                      };
                      navigateTo(pageMap[cat.key] || "medicines", e);
                    }
                  }}
                >
                  <div className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white border border-blue-200 hover:border-blue-400">
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-blue-800 text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cat_${cat.key}`} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="medicines"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Products - Medical Cards */}
          <section className="w-full px-4 md:px-8 py-8 bg-white/70 backdrop-blur-sm border-y-2 border-blue-200/30">
            <div className="text-center mb-10">
              <span className="text-blue-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Top Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Essential Healthcare Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableMedicalProducts.slice(0, 4).map((product, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={product.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-blue-200 hover:border-blue-400">
                      <div className="relative h-48 overflow-hidden bg-blue-50">
                        <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </InlineEditableImage>
                        {product.badge && (
                          <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                            {product.badge}
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-blue-700/80 text-blue-100 text-[10px] font-bold px-3 py-1 rounded-full">
                          {product.quantity}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-gray-500">{product.brand}</span>
                          <span className="text-xs text-gray-300">•</span>
                          <span className="text-xs text-gray-500">{product.category}</span>
                        </div>
                        <h3 className="font-semibold text-blue-900 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.nameKey} 
                            value={product.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-blue-700">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={product.priceKey} 
                              value={product.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {product.originalPrice > product.price && (
                              <span className="text-xs text-gray-400 line-through ml-1">₹{product.originalPrice}</span>
                            )}
                          </div>
                          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                            {product.subCategory}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => navigateTo("medicines", e)} 
                className="px-8 py-3 rounded-full bg-blue-700 text-white font-semibold hover:bg-blue-800 transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-700/20"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View All Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="medicines"
                />
              </button>
            </div>
          </section>
          
          {/* Health Journal Preview */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-8">
              <span className="text-blue-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="journal_badge" 
                  value="Health Journal"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="journal_title" 
                  value="Latest Health Insights"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                />
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {editableHealthPosts.slice(0, 3).map((post) => (
                <div 
                  key={post.id} 
                  className="group cursor-pointer rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-blue-200 hover:border-blue-400 animate-card-fly"
                  onClick={(e) => openBlogModal(post, e)}
                >
                  <div className="h-40 overflow-hidden">
                    <InlineEditableImage p={p} field={post.imageField} className="block h-full w-full overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-blue-600 font-semibold">{post.category}</span>
                    </div>
                    <h4 className="font-semibold text-blue-900 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.titleKey} 
                        value={post.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </h4>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.descKey} 
                        value={post.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </p>
                    <div className="mt-3 text-blue-700 text-xs font-semibold group-hover:gap-2 transition-all flex items-center gap-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== MEDICINES PAGE ==================== */}
      {activePage === "medicines" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-blue-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="medicines_badge" 
                value="Medicines"
                onNavigate={handleNavigateAfterEdit}
                navPage="medicines"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="medicines_title" 
                value="Trusted Medicines"
                onNavigate={handleNavigateAfterEdit}
                navPage="medicines"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="medicines_subtitle" 
                value="Quality medications for your family's health"
                onNavigate={handleNavigateAfterEdit}
                navPage="medicines"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableMedicalProducts.filter(d => d.category === "Medicines").map((product, idx) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-blue-200 hover:border-blue-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.06}s` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-blue-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-blue-700/80 text-blue-100 text-[10px] font-bold px-3 py-1 rounded-full">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-blue-900 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="medicines"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-blue-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="medicines"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Dosage: {product.dosage} • {product.type}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== VITAMINS PAGE ==================== */}
      {activePage === "vitamins" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="vitamins_badge" 
                value="Vitamins"
                onNavigate={handleNavigateAfterEdit}
                navPage="vitamins"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-green-800 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="vitamins_title" 
                value="Premium Supplements"
                onNavigate={handleNavigateAfterEdit}
                navPage="vitamins"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="vitamins_subtitle" 
                value="Essential nutrients for optimal health"
                onNavigate={handleNavigateAfterEdit}
                navPage="vitamins"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-green-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableMedicalProducts.filter(d => d.category === "Vitamins").map((product, idx) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-green-200 hover:border-green-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.06}s` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-green-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-green-700/80 text-green-100 text-[10px] font-bold px-3 py-1 rounded-full">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-green-900 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="vitamins"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-green-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="vitamins"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Dosage: {product.dosage} • {product.type}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== EQUIPMENT PAGE ==================== */}
      {activePage === "equipment" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-purple-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="equipment_badge" 
                value="Equipment"
                onNavigate={handleNavigateAfterEdit}
                navPage="equipment"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-purple-800 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="equipment_title" 
                value="Medical Equipment"
                onNavigate={handleNavigateAfterEdit}
                navPage="equipment"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="equipment_subtitle" 
                value="Professional-grade medical devices for home use"
                onNavigate={handleNavigateAfterEdit}
                navPage="equipment"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-purple-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableMedicalProducts.filter(d => d.category === "Equipment").map((product, idx) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-purple-200 hover:border-purple-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.06}s` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-purple-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-purple-700/80 text-purple-100 text-[10px] font-bold px-3 py-1 rounded-full">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-purple-900 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="equipment"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-purple-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="equipment"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {product.type} • Warranty: {product.warranty}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== PERSONAL CARE PAGE ==================== */}
      {activePage === "personal_care" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="personal_care_badge" 
                value="Personal Care"
                onNavigate={handleNavigateAfterEdit}
                navPage="personal_care"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-800 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="personal_care_title" 
                value="Personal Care Essentials"
                onNavigate={handleNavigateAfterEdit}
                navPage="personal_care"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="personal_care_subtitle" 
                value="Products for your daily health and hygiene"
                onNavigate={handleNavigateAfterEdit}
                navPage="personal_care"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableMedicalProducts.filter(d => d.category === "Personal Care").map((product, idx) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-amber-200 hover:border-amber-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.06}s` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden bg-amber-50">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-3 py-1 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-amber-700/80 text-amber-100 text-[10px] font-bold px-3 py-1 rounded-full">
                    {product.quantity}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500">{product.brand}</span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{product.subCategory}</span>
                  </div>
                  <h3 className="font-semibold text-amber-900 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="personal_care"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-amber-700">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="personal_care"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {product.type} • {product.protection || product.items || "Daily Care"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== HEALTH TIPS PAGE ==================== */}
      {activePage === "health" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-pink-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="health_page_badge" 
                value="Health Tips"
                onNavigate={handleNavigateAfterEdit}
                navPage="health"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-pink-800 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="health_page_title" 
                value="Daily Health Tips"
                onNavigate={handleNavigateAfterEdit}
                navPage="health"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="health_page_subtitle" 
                value="Expert advice for a healthier life"
                onNavigate={handleNavigateAfterEdit}
                navPage="health"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-600 to-pink-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {healthTips.map((tip, idx) => (
              <div 
                key={tip.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-pink-200 hover:border-pink-400 animate-card-fly"
                style={{ animationDelay: `${idx * 0.08}s` }}
                onClick={() => openHealthTipModal(tip)}
              >
                <div className="p-6 text-center">
                  <div className="text-6xl mb-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
                    {tip.icon}
                  </div>
                  <h3 className="text-xl font-bold text-pink-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={tip.titleKey} 
                      value={tip.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="health"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={tip.descKey} 
                      value={tip.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="health"
                    />
                  </p>
                  <div className="mt-4 text-pink-700 font-semibold flex items-center justify-center gap-1 group-hover:gap-2 transition-all">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="learn_more_button" 
                      value="Learn More"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="health"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-blue-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_page_badge" 
                value="Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_page_title" 
                value="Health & Wellness Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_page_subtitle" 
                value="Expert insights for better health"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editableHealthPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-blue-200 hover:border-blue-400"
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={post.imageField} className="block h-full w-full overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-blue-600 font-semibold">{post.category}</span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-blue-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <span className="font-semibold text-blue-800">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={post.authorKey} 
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="journal"
                        />
                      </span>
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-blue-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-blue-700 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="About MediCare"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-900 mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Your Trusted Health Partner"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-blue-600 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="MediCare Pharmacy is a trusted healthcare provider dedicated to offering high-quality medical products and services. Our mission is to make healthcare accessible, affordable, and convenient for every family."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="With a commitment to authenticity and customer care, we provide only verified medicines and healthcare products. Our team of qualified pharmacists ensures that you receive the best advice and products for your health needs."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-blue-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="500+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Products"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="1000+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Happy Customers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="10+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Years of Trust"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-blue-200/50">
              <InlineEditableImage p={p} field="medicareAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.medicareAboutImage || "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5e971433-f614-4bfe-9da4-08f9957bc803_med4.jpg"} 
                  alt="About MediCare" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "💊 Shop Medicines"],
            ["services", "🏥 Health Services"],
            ["details", "📖 About MediCare"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-blue-700 text-white shadow-lg" : "text-gray-500 hover:text-blue-700"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Product Modal */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-3xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedProduct.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-blue-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedProduct.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedProduct.badgeColor }}>
                  {selectedProduct.badge}
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-blue-700/80 text-blue-100 text-xs font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                {selectedProduct.quantity}
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">{selectedProduct.brand}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{selectedProduct.category}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-500">{selectedProduct.subCategory}</span>
              </div>
              <h3 className="text-2xl font-bold text-blue-900">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.nameKey} 
                  value={selectedProduct.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.descriptionKey} 
                  value={selectedProduct.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full border border-blue-200">
                  {selectedProduct.subCategory}
                </span>
                {selectedProduct.dosage && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    💊 {selectedProduct.dosage}
                  </span>
                )}
                {selectedProduct.type && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    {selectedProduct.type}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-blue-700">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.priceKey} 
                    value={selectedProduct.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedProduct.originalPrice > selectedProduct.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedProduct.originalPriceKey} 
                      value={selectedProduct.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Health Tip Modal */}
      {selectedHealthTip && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-3xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 text-center">
              <div className="text-8xl mb-4 animate-bounce">{selectedHealthTip.icon}</div>
              <h3 className="text-2xl font-bold text-blue-900 mb-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedHealthTip.titleKey} 
                  value={selectedHealthTip.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="health"
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-base leading-relaxed">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedHealthTip.descKey} 
                  value={selectedHealthTip.desc}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="health"
                  isInModal={true}
                />
              </p>
              <div className="mt-6">
                <button 
                  onClick={closeHealthTipModal} 
                  className="px-6 py-2.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Got it!"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="health"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-blue-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-blue-900">Health Journal</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-blue-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-semibold border border-blue-200">
                  {selectedPost.category}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-blue-100 pb-4 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-blue-800">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-blue-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee-slow { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee-slow { animation: marquee-slow 25s linear infinite; }
        
        @keyframes medical-particle-float { 
          0% { transform: translate(0, 0) scale(1); opacity: 0.03; }
          50% { transform: translate(15px, -25px) scale(1.5); opacity: 0.08; }
          100% { transform: translate(0, -50px) scale(1); opacity: 0.03; }
        }
        .medical-particle { animation: medical-particle-float 6s ease-in-out infinite; }
        .plus-float { border-radius: 2px; }
        .circle-float { border-radius: 50%; }
        
        @keyframes medical-symbol-float {
          0% { transform: translateY(0) rotate(0deg) scale(1); }
          50% { transform: translateY(-30px) rotate(10deg) scale(1.2); }
          100% { transform: translateY(0) rotate(0deg) scale(1); }
        }
        .medical-symbol-float { animation: medical-symbol-float 10s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9) rotate(-2deg); } 60% { transform: translateY(-5px) scale(1.02) rotate(1deg); } 100% { opacity: 1; transform: translateY(0) scale(1) rotate(0); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}